import React, { useEffect, useRef, useState } from 'react'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Box,
  Typography, Avatar, Stack, Slider, CircularProgress
} from '@mui/material'
import { type FileRejection, useDropzone } from 'react-dropzone'
import UploadIcon from '@mui/icons-material/Upload'
import Compressor from 'compressorjs'
import Cropper, { type Area } from 'react-easy-crop'
import { IconButtonWithTooltip, useGlobalMessageHandler } from 'components'
import { type PersonPictureInformation, usNewPersonPicture } from 'services'
import { formatErrorFieldMessages } from '../../../../../../interfaces'

interface UploadLogoProps {
  personId: number
  setImageLink: (imageLink: PersonPictureInformation) => void
}

// The starting quality for image compression. The Compressor will lower the quality until the image is less than 5KB.
const STARTING_QUALITY = 0.8

const MAX_IMAGE_SIZE = 50 * 1000000 // 50 MB

const COMPRESSED_SIZE = 20000 // 20KB

const IMAGE_WIDTH = 400
const IMAGE_HEIGHT = 400

const CROP_ASPECT_W = 108
const CROP_ASPECT_H = 148
const MIN_CROP_WIDTH = 108

const ACCEPTED_IMAGE_TYPES = {
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/png': ['.png'],
  'image/gif': ['.gif'],
  'image/webp': ['.webp'],
  'image/svg+xml': ['.svg']
}

const UploadBinaryLogo: React.FC<UploadLogoProps> = ({ personId, setImageLink }) => {
  const [open, setOpen] = useState(false)
  const [isCompressing, setIsCompressing] = useState(false)
  const [isSending, setIsSending] = useState(false)
  const [fileData, setFileData] = useState<{ file?: File, fileBase64?: string }>({})
  const { showSuccessMessage, showErrorMessage, clearMessages } = useGlobalMessageHandler()
  const [quality, setQuality] = useState(STARTING_QUALITY)
  const [cropSrc, setCropSrc] = useState<string | undefined>(undefined)
  const [crop, setCrop] = useState<{ x: number, y: number }>({ x: 0, y: 0 })
  const [zoom, setZoom] = useState(1)
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null)
  const sessionRef = useRef(0)

  useEffect(() => {
    const session = sessionRef.current
    const compressFile = async (file: File, quality: number, maxWidth: number, maxHeight: number): Promise<{ file: File, fileBase64: string }> => {
      return await new Promise((resolve, reject) => {
        // eslint-disable-next-line no-new
        new Compressor(file, {
          quality,
          maxWidth,
          maxHeight,
          convertSize: 0,
          success (result: Blob) {
            const reader = new FileReader()
            reader.readAsArrayBuffer(result)
            reader.onloadend = () => {
              if (reader.result != null) {
                const compressedFile = new File([reader.result], file.name + '_compressed', { type: result.type })
                if (compressedFile.size > COMPRESSED_SIZE && quality > 0.1) {
                  setQuality(quality - 0.1)
                  resolve(compressFile(file, quality - 0.1, maxWidth, maxHeight))
                } else {
                  resolve(getBase64(compressedFile).then(base64str => ({ file: compressedFile, fileBase64: base64str })))
                }
              }
            }
          },
          error (err: Error) {
            reject(err)
          }
        })
      })
    }
    if (fileData.file != null) {
      void getBase64(fileData.file).then(async base64str => {
        if (session !== sessionRef.current) { return }
        if (base64str !== fileData.fileBase64 && fileData.file != null) {
          setIsCompressing(true)
          await compressFile(fileData.file, STARTING_QUALITY, IMAGE_WIDTH, IMAGE_HEIGHT).then(value => {
            if (session === sessionRef.current) {
              setFileData({
                file: value.file,
                fileBase64: value.fileBase64
              })
            }
          }).finally(() => {
            if (session === sessionRef.current) {
              setIsCompressing(false)
            }
          })
        }
      }).catch(() => {
        if (session === sessionRef.current) {
          setIsCompressing(false)
          showErrorMessage('Failed to process the image. Please try again.')
        }
      })
    }
  }, [fileData.file])

  const showFormatError = (): void => { showErrorMessage('Failed to load this image format. Please try a different one.') }

  const onDrop = (acceptedFiles: File[], rejectedFiles: FileRejection[]): void => {
    clearMessages()
    if (rejectedFiles.length > 0) {
      showErrorMessage(rejectedFiles[0].errors[0].message ?? 'Failed to upload image')
      return
    }
    const newFile = acceptedFiles[0]
    if (newFile == null) {
      return
    }
    if (newFile.size > MAX_IMAGE_SIZE || newFile.size < 1000) {
      showErrorMessage('Image size is invalid. Please upload a file from 1kb to 50 Mb.')
      return
    }
    sessionRef.current += 1
    const session = sessionRef.current
    void getBase64(newFile).then(dataUrl => {
      const img = new Image()
      img.onload = () => {
        if (session !== sessionRef.current) { return }
        if (Math.min(img.width, img.height * CROP_ASPECT_W / CROP_ASPECT_H) < MIN_CROP_WIDTH) {
          showErrorMessage(`Image is too small. Please upload an image at least ${MIN_CROP_WIDTH}x${Math.round(MIN_CROP_WIDTH * CROP_ASPECT_H / CROP_ASPECT_W)} pixels.`)
          return
        }
        setCrop({ x: 0, y: 0 })
        setZoom(1)
        setCroppedAreaPixels(null)
        setFileData({})
        setCropSrc(dataUrl)
      }
      img.onerror = showFormatError
      img.src = dataUrl
    }).catch(showFormatError)
  }

  const { getRootProps, getInputProps } = useDropzone({ onDrop, multiple: false, maxSize: MAX_IMAGE_SIZE, maxFiles: 1, accept: ACCEPTED_IMAGE_TYPES })

  const handleClose = (): void => {
    sessionRef.current += 1
    setOpen(false)
    setFileData({})
    setCropSrc(undefined)
    setIsCompressing(false)
  }

  const hasFile = fileData.file != null

  const saveImage = (): void => {
    if (fileData.fileBase64 != null) {
      setIsSending(true)
      usNewPersonPicture(fileData.fileBase64.split(',')[1], personId).then(value => {
        if (value.result != null) {
          showSuccessMessage('Image Uploaded')
          setImageLink(value.result)
          handleClose()
        }
      }).catch(reason => {
        showErrorMessage(formatErrorFieldMessages(reason.response.data.errors))
      })
        .finally(() => { setIsSending(false) })
    }
  }
  const getBase64 = async (file: File): Promise<string> =>
    await new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => { resolve(reader.result != null ? reader.result as string : 'Error') }
      reader.onerror = reject
      reader.readAsDataURL(file)
    })

  const getCroppedImg = async (src: string, area: Area): Promise<File> =>
    await new Promise((resolve, reject) => {
      const image = new Image()
      image.onload = () => {
        const canvas = document.createElement('canvas')
        canvas.width = CROP_ASPECT_W
        canvas.height = CROP_ASPECT_H
        const ctx = canvas.getContext('2d')
        if (ctx == null) {
          reject(new Error('Could not process image'))
          return
        }
        ctx.fillStyle = '#ffffff'
        ctx.fillRect(0, 0, CROP_ASPECT_W, CROP_ASPECT_H)
        ctx.drawImage(image, area.x, area.y, area.width, area.height, 0, 0, CROP_ASPECT_W, CROP_ASPECT_H)
        canvas.toBlob(blob => {
          if (blob == null) {
            reject(new Error('Could not process image'))
            return
          }
          resolve(new File([blob], 'photo.jpg', { type: 'image/jpeg' }))
        }, 'image/jpeg', 0.95)
      }
      image.onerror = () => { reject(new Error('Could not load image')) }
      image.src = src
    })

  const applyCrop = async (): Promise<void> => {
    if (cropSrc == null || croppedAreaPixels == null) {
      return
    }
    if (croppedAreaPixels.width < MIN_CROP_WIDTH - 1) {
      showErrorMessage(`Selected area is too small. Zoom out to keep at least ${MIN_CROP_WIDTH}px wide.`)
      return
    }
    const session = sessionRef.current
    try {
      const croppedFile = await getCroppedImg(cropSrc, croppedAreaPixels)
      if (session !== sessionRef.current) { return }
      setCropSrc(undefined)
      setQuality(STARTING_QUALITY)
      setFileData({ file: croppedFile })
    } catch (error) {
      showErrorMessage('Failed to crop the image. Please try again.')
    }
  }

  return (
        <div>
            <label htmlFor="upload-photo">
                <IconButtonWithTooltip
                    label="Upload Photo"
                    color="primary"
                    onClick={() => { setOpen(true) }}
                    icon={<UploadIcon />}
                />
            </label>
            <Dialog open={open} onClose={handleClose}>
                <DialogTitle>Upload Photo</DialogTitle>
                <DialogContent sx={{ minWidth: '11.8vw', minHeight: '26.3vh', display: 'flex', justifyContent: 'center' }}>
                  {cropSrc != null && !isCompressing && (
                    <Stack direction="column" spacing={2} sx={{ width: 300 }}>
                      <Box sx={{ position: 'relative', width: 300, height: 300 }}>
                        <Cropper image={cropSrc} crop={crop} zoom={zoom} aspect={CROP_ASPECT_W / CROP_ASPECT_H} onCropChange={setCrop} onZoomChange={setZoom} onCropComplete={(_croppedArea, croppedPixels) => { setCroppedAreaPixels(croppedPixels) }} />
                      </Box>
                      <Slider value={zoom} min={1} max={3} step={0.1} aria-label="Zoom" onChange={(_event, value) => { setZoom(value as number) }} />
                    </Stack>
                  )}
                  {cropSrc == null && !isCompressing && (
                  <Box {...getRootProps()} sx={{ border: !hasFile ? '2px dashed #ccc' : 'none', padding: 2, textAlign: 'center', alignContent: 'center', cursor: 'pointer' }}>
                  <input {...getInputProps()} />
                  {!hasFile &&
                        <Typography>Drag & drop an image here
                          <br/>Or click to select one from your computer</Typography>
                  }
                  {hasFile && (
                    <Stack direction="column" justifyContent="center" alignItems="center" spacing={2}>
                      <Avatar src={fileData.fileBase64} sx={{ width: 125, height: 125 }} />
                      <Typography>
                        {fileData.file != null &&
                          <React.Fragment>
                            {fileData.file.size < COMPRESSED_SIZE && '✅ '}
                            {(fileData.file.size / 1024).toFixed(2)} KB
                          </React.Fragment>
                        }
                      </Typography>
                    </Stack>

                  )}
                    </Box>
                  )}
                  {isCompressing && <Stack direction="column" justifyContent="center" alignItems="center" spacing={2}>
                    <CircularProgress size={70} />
                    <Typography>Compressing {'again '.repeat(Math.floor((STARTING_QUALITY - quality) * 10))}</Typography>
                  </Stack>}
                </DialogContent>
                <DialogActions>
                  {isSending && <CircularProgress sx={{ mr: 2.3 }} size={25}/>}
                  {!isSending && (cropSrc != null
                    ? <React.Fragment>
                        <Button onClick={() => { setCropSrc(undefined) }} color="secondary">Back</Button>
                        <Button onClick={() => { void applyCrop() }} variant="contained" color="primary" disabled={croppedAreaPixels == null}>Apply</Button>
                      </React.Fragment>
                    : <React.Fragment>
                        <Button onClick={handleClose} color="secondary">Close</Button>
                        <Button onClick={saveImage} variant="contained" disabled={fileData.fileBase64 == null} color="primary">Save</Button>
                      </React.Fragment>)}
                </DialogActions>
            </Dialog>
        </div>
  )
}

export default UploadBinaryLogo
